import { FC, useCallback, useEffect, useMemo, useState } from 'react';
import {
    Button,
    cssX,
    includesInLC,
    markMatch,
    SelectComponent,
    TextInput,
    toggleInArray,
} from '@datassential/platform-ui-lib';
import { FiSearch } from 'react-icons/fi';
import { FaCheck } from 'react-icons/fa';
import { IoIosArrowUp } from 'react-icons/io';
import cn from 'classnames';
import { minusArrayUnique, uniteArraysUnique } from '@helpers';
import { CustomBaseScrollbars } from '@components/CustomScrollbarsVirtualList/CustomScrollbarsVirtualList';
import './GroupedMultiSelect.scss';

export interface GroupedMultiSelectOption {
    id: number;
    name: string;
}

export interface GroupedMultiSelectGroup {
    id: number;
    name: string;
    options: GroupedMultiSelectOption[];
}

interface Props {
    value: number[];
    setValue: (value: number[]) => void;
    groups: GroupedMultiSelectGroup[];
    placeholder?: string;
    unitLabel?: string;
    width?: number;
}

const Toggle: FC<{
    isChecked: boolean;
    onToggle: () => void;
    /** Plain-text accessible name — the visible label is rendered as highlighted HTML. */
    label: string;
    isPartial?: boolean;
    children: React.ReactNode;
}> = ({ isChecked, onToggle, label, isPartial = false, children }) => {
    const handleKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
        if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            onToggle();
        }
    };

    return (
        <div
            className={cn(
                'GroupedMultiSelectToggle',
                isChecked && 'is-checked',
                isPartial && 'is-partial',
            )}
            role="checkbox"
            aria-checked={isPartial ? 'mixed' : isChecked}
            aria-label={label}
            tabIndex={0}
            onClick={onToggle}
            onKeyDown={handleKeyDown}
        >
            <div className="GroupedMultiSelectToggle__icon">
                <FaCheck />
            </div>
            {children}
        </div>
    );
};

const GroupedMultiSelect: FC<Props> = ({
    value,
    setValue,
    groups,
    placeholder = 'Select',
    unitLabel = 'Items',
    width = 300,
}) => {
    const [isOpened, setIsOpened] = useState(false);
    const [expandedGroupIds, setExpandedGroupIds] = useState<number[]>([]);
    const [search, setSearch] = useState('');

    useEffect(() => {
        if (!isOpened) setSearch('');
    }, [isOpened]);

    const isSearchMode = search.trim() !== '';

    const filteredGroups = useMemo(() => {
        if (!isSearchMode) return groups;
        return groups
            .filter((group) => group.options.some((opt) => includesInLC(opt.name, search)))
            .map((group) => ({
                ...group,
                options: group.options.filter((opt) => includesInLC(opt.name, search)),
            }));
    }, [groups, search]);

    const thumbnailText = useMemo(() => {
        if (value.length === 0) {
            return <div className="GroupedMultiSelect__placeholder">{placeholder}</div>;
        }

        const selectedGroups = groups.filter((group) =>
            group.options.some((o) => value.includes(o.id)),
        );

        if (selectedGroups.length === 1) {
            const group = selectedGroups[0];
            const selectedOptions = group.options.filter((o) => value.includes(o.id));
            const selectedCount = selectedOptions.length;

            if (selectedCount === 1) return `${selectedOptions[0].name}`;
            if (selectedCount === group.options.length) return `${group.name}`;
            return `${group.name} (${selectedCount})`;
        }

        return `${selectedGroups.length} ${unitLabel}`;
    }, [value, groups, placeholder, unitLabel]);

    const handleToggleGroup = useCallback((groupId: number) => {
        setExpandedGroupIds((prev) => toggleInArray(prev, groupId));
    }, []);

    const handleGroupKeyDown = useCallback((e: React.KeyboardEvent, groupId: number) => {
        if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            setExpandedGroupIds((prev) => toggleInArray(prev, groupId));
        }
    }, []);

    return (
        <div className="GroupedMultiSelect" style={{ width }}>
            <SelectComponent
                isOpened={isOpened}
                setIsOpened={setIsOpened}
                thumbnailText={thumbnailText}
                dropdownProps={{
                    isOpened: isOpened,
                    place: 'bottom-start',
                    style: { height: 350, overflowX: 'hidden', display: 'block', width, padding: 6 },
                }}
            >
                <CustomBaseScrollbars>
                    <div className="GroupedMultiSelect__dropdown-content">
                        <div className="GroupedMultiSelect__actions">
                            <Button
                                onClick={() => setValue([])}
                                modifiers={['naked']}
                                isDisabled={value.length === 0}
                            >
                                clear all
                            </Button>
                        </div>
                        <div className={cn(cssX('SelectComponent__search'), 'GroupedMultiSelect__search')}>
                            <FiSearch className={cssX('SelectComponent__search-icon')} />
                            <TextInput
                                placeholder="Search"
                                value={search}
                                setValue={setSearch}
                                autoFocus
                            />
                        </div>
                        <div className="GroupedMultiSelect__options">
                            {filteredGroups.map((group) => {
                                const sourceGroup = groups.find((g) => g.id === group.id);
                                const allOptionIds = sourceGroup ? sourceGroup.options.map((o) => o.id) : [];
                                const selectedOptionIds = allOptionIds.filter((id) => value.includes(id));
                                const isAllSelected = allOptionIds.length > 0 && allOptionIds.length === selectedOptionIds.length;
                                const isAnySelected = selectedOptionIds.length > 0;

                                return (
                                    <div
                                        key={group.id}
                                        className={cn(
                                            'GroupedMultiSelect__group',
                                            !isSearchMode && !expandedGroupIds.includes(group.id) && 'is-collapsed',
                                        )}
                                    >
                                        <div className="GroupedMultiSelect__row">
                                            <Toggle
                                                isChecked={isAnySelected}
                                                isPartial={isAnySelected && !isAllSelected}
                                                label={group.name}
                                                onToggle={() => setValue((isAllSelected ? minusArrayUnique : uniteArraysUnique)(value, allOptionIds))}
                                            >
                                                <div dangerouslySetInnerHTML={{ __html: markMatch(group.name, search.trim()) }} />
                                            </Toggle>
                                            {!isSearchMode && (
                                                <IoIosArrowUp
                                                    className="GroupedMultiSelect__group-toggle"
                                                    role="button"
                                                    tabIndex={0}
                                                    aria-label={`Toggle ${group.name} options`}
                                                    onClick={() => handleToggleGroup(group.id)}
                                                    onKeyDown={(e) => handleGroupKeyDown(e, group.id)}
                                                />
                                            )}
                                        </div>
                                        <div className="GroupedMultiSelect__group-options">
                                            {group.options.map((option) => (
                                                <div key={option.id} className="GroupedMultiSelect__row is-nested">
                                                    <Toggle
                                                        isChecked={value.includes(option.id)}
                                                        label={option.name}
                                                        onToggle={() => setValue(toggleInArray(value, option.id))}
                                                    >
                                                        <div dangerouslySetInnerHTML={{ __html: markMatch(option.name, search.trim()) }} />
                                                    </Toggle>
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                );
                            })}
                            {isSearchMode && filteredGroups.length === 0 && (
                                <div className={cssX('SearchBox__no-results')}>
                                    Nothing found.
                                    <br />
                                    Try searching something else.
                                </div>
                            )}
                        </div>
                    </div>
                </CustomBaseScrollbars>
            </SelectComponent>
        </div>
    );
};

export default GroupedMultiSelect;
